import React, { useEffect, useState } from "react";
import Label from "./Label";
import BetterNumberInput from "~/components/forms/BetterNumberInput";
import SelectInput from "~/components/forms/SelectInput";
import { useTranslation } from "react-i18next";

type TemperatureConverterProps = {
  valueA: number;
  unitA: "C" | "F" | "K";
  unitB: "C" | "F" | "K";
};

const TemperatureConverter: React.FC<TemperatureConverterProps> = (
  {
    valueA: initialValueA,
    unitA: initialUnitA,
    unitB: initialUnitB
  }
) => {
  const { t } = useTranslation("translations");
  const TKEY: string = "calculators.temperature";

  const [valueA, setValueA] = useState<number>(initialValueA);
  const [valueB, setValueB] = useState<number>(0);
  const [unitA, setUnitA] = useState<"C" | "F" | "K">(initialUnitA);
  const [unitB, setUnitB] = useState<"C" | "F" | "K">(initialUnitB);
  const [showAbsoluteZeroWarning, setShowAbsoluteZeroWarning] = useState<boolean>(false);

  const unitOptions = [
    { name: t(`${TKEY}.units.celsius`), value: "C" },
    { name: t(`${TKEY}.units.fahrenheit`), value: "F" },
    { name: t(`${TKEY}.units.kelvin`), value: "K" }
  ];

  const roundToTwoDecimals = (value: number): number => Math.round(value * 100) / 100;

  const convertTemperature = (
    value: number,
    fromUnit: "C" | "F" | "K",
    toUnit: "C" | "F" | "K"
  ): number => {
    if (fromUnit === toUnit) return value;

    // Convert fromUnit to Celsius first
    let celsiusValue = value;
    if (fromUnit === "F") {
      celsiusValue = (value - 32) * (5 / 9);
    } else if (fromUnit === "K") {
      celsiusValue = value - 273.15;
    }

    // Convert Celsius to toUnit
    let result = celsiusValue;
    if (toUnit === "F") {
      result = celsiusValue * (9 / 5) + 32;
    } else if (toUnit === "K") {
      result = celsiusValue + 273.15;
    }
    return roundToTwoDecimals(result);
  };

  // Ensure temperature values do not go below absolute zero
  const enforceAbsoluteZero = (value: number, unit: "C" | "F" | "K"): number => {
    const minValue = {
      C: -273.15,
      F: -459.67,
      K: 0
    };

    if (value < minValue[unit]) {
      setShowAbsoluteZeroWarning(true);
      return minValue[unit];
    } else {
      // This is necessary to prevent hiding the warning instantly
      // when the value is changed to the minimum value programmatically on invalid input
      if (value !== minValue[unit]) {
        setShowAbsoluteZeroWarning(false);
      }
      return value;
    }
  };

  const handleValueAChange = (value: string) => {
    setShowAbsoluteZeroWarning(true);

    const numericValue = parseFloat(value);
    const validValueA = enforceAbsoluteZero(numericValue, unitA);
    const convertedValue = convertTemperature(validValueA, unitA, unitB);

    // Prevent looping update
    if (valueA === validValueA) return;

    console.debug("Setting values in A handler", validValueA, convertedValue);
    setValueA(validValueA);
    setValueB(convertedValue);
  };

  const handleValueBChange = (value: string) => {
    const numericValue = parseFloat(value);
    const validValueB = enforceAbsoluteZero(numericValue, unitB);
    const convertedValue = convertTemperature(validValueB, unitB, unitA);

    // Prevent looping update
    if (valueB === validValueB) return;

    console.debug("Setting values in B handler", validValueB, convertedValue);
    setValueB(validValueB);
    setValueA(convertedValue);
  };

  const handleUnitAChange = (newUnit: string) => {
    const unit = newUnit as "C" | "F" | "K";
    setUnitA(unit);
    const validValueA = enforceAbsoluteZero(valueA, unit);
    setValueA(validValueA);
    const convertedValue = convertTemperature(validValueA, unit, unitB);
    setValueB(convertedValue);
  };

  const handleUnitBChange = (newUnit: string) => {
    const unit = newUnit as "C" | "F" | "K";
    setUnitB(unit);
    const convertedValue = convertTemperature(valueA, unitA, unit);
    setValueB(convertedValue);
  };

  useEffect(() => {
    const initialConvertedValue = convertTemperature(initialValueA, initialUnitA, initialUnitB);
    setValueB(initialConvertedValue);
  }, [initialValueA, initialUnitA, initialUnitB]);

  return (
    <div className="flex-col">
      <div className="flex flex-col gap-4  md:flex-row md:gap-6 items-center">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="w-full md:max-w-[180px]">
            <Label text={t(`${TKEY}.from`)}>
              <BetterNumberInput
                name="valueA"
                value={valueA}
                onChange={handleValueAChange}
                min={unitA === "C" ? -273.15 : unitA === "F" ? -459.67 : 0}
                maxFractionDigits={2}
                ariaLabel={t(`${TKEY}.to`)}
                // autoFocus={true}
              />
            </Label>
          </div>
          <div className="w-full md:max-w-[200px]">
            <Label text={t(`${TKEY}.unit`)}>
              <SelectInput
                name="unitA"
                options={unitOptions}
                value={unitOptions.find((unit) => unit.value === unitA)?.value}
                ariaLabel={t(`${TKEY}.unit`)}
                onChange={handleUnitAChange}
              />
            </Label>
          </div>
        </div>
        <div className="text-6xl pt-4 text-slate-500 dark:text-slate-400">
        <span className="hidden md:inline">
          ⬌
        </span>
          <span className="md:hidden">
          ⬍
        </span>
        </div>
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="w-full md:max-w-[180px]">
            <Label text={t(`${TKEY}.to`)}>
              <BetterNumberInput
                name="valueB"
                value={valueB}
                onChange={handleValueBChange}
                min={unitB === "C" ? -273.15 : unitB === "F" ? -459.67 : 0}
                maxFractionDigits={2}
                ariaLabel={t(`${TKEY}.to`)}
              />
            </Label>
          </div>
          <div className="w-full md:max-w-[200px]">
            <Label text={t(`${TKEY}.unit`)}>
              <SelectInput
                name="unitB"
                options={unitOptions}
                value={unitOptions.find((unit) => unit.value === unitB)?.value}
                ariaLabel={t(`${TKEY}.unit`)}
                onChange={handleUnitBChange}
              />
            </Label>
          </div>
        </div>
      </div>
      {showAbsoluteZeroWarning && (
        <div className="text-red-600 text-sm pt-4">
          {t(`${TKEY}.absolute_zero_warning`)}
        </div>
      )}
    </div>
  );
};

export default TemperatureConverter;